import React, { ReactNode, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import ChevronUpIcon from "@heroicons/react/24/outline/ChevronUpIcon";
import Dropdown from "@/components/views/account/settings/interests/dropdown/Dropdown";
import {
  QuoteAutocompleteProvider,
  useQuoteAutocomplete,
} from "@/context/quote-venue/QuoteAutocompleteContext";

interface QuoteMainProps {
  icon: any;
  isFocused: boolean;
  selectedData: any;
  setSelectedData: (district: any) => void;
  closeDropdown: () => void;
  children: any;
  className?: string;
}

export function QuoteMain(props: QuoteMainProps) {
  const {
    icon,
    children,
    setSelectedData,
    closeDropdown,
    selectedData,
    isFocused,
  } = props;
  // const dropdownRef = useRef<HTMLDivElement>(null);
  //
  // useEffect(() => {
  //   function handleClickOutside(event: MouseEvent) {
  //     if (
  //       dropdownRef.current &&
  //       !dropdownRef.current.contains(event.target as Node)
  //     ) {
  //       closeDropdown();
  //     }
  //   }
  //
  //   if (isFocused) {
  //     document.addEventListener("mousedown", handleClickOutside);
  //   }
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, [isFocused]);

  return (
    <div className={cn("relative h-auto w-full z-20 left-0", props.className)}>
      <div
        // ref={dropdownRef}
        className={cn(
          "w-full bg-white flex-col justify-start items-start inline-flex",
          isFocused
            ? "md:h-72 h-96 border border-slate-300 bg-white rounded-[20px] absolute"
            : "h-auto",
          !selectedData &&
            !isFocused &&
            `border rounded-[20px] border-secondary`,
        )}
      >
        <QuoteAutocompleteProvider
          icon={icon}
          setSelectedData={setSelectedData}
          isFocused={isFocused}
          selectedData={selectedData}
        >
          {children}
        </QuoteAutocompleteProvider>
      </div>
    </div>
  );
}

interface QuoteInputProps {
  parentId: string;
  id: string;
  query: string;
  label: string;
  isFocusedColor: string;
  onChange: (e: ReactEvents.ChangeEvent<HTMLInputElement>) => void;
  onFocus: (event: any) => void;
}

export function QuoteInput(props: QuoteInputProps) {
  const { id, parentId, query, isFocusedColor, label, onChange, onFocus } = props
  const { isFocused, icon } = useQuoteAutocomplete()

  const Icon = icon
  const inputRef = useRef<HTMLInputElement>(null)
  const isManualFocus = useRef(false)

  useEffect(() => {
    if (isFocused && inputRef.current && document.activeElement !== inputRef.current) {
      isManualFocus.current = true
      inputRef.current.focus()
    }
  }, [isFocused])

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!isManualFocus.current) {
      onFocus(event)
    }
    isManualFocus.current = false
  }

  return (
    <div
      id={parentId}
      onClick={() => inputRef.current?.focus()}
      className={cn(
        "py-2 cursor-pointer h-14 w-full rounded-[20px] duration-200 border px-4 gap-2 flex justify-between items-center shadow-lg",
        isFocused ? `border-${isFocusedColor}` : "border-slate-300"
      )}
    >
      {isFocused && icon && (
        <div className={cn("!h-9 flex duration-200 justify-center items-center text-2xl")}>
          <Icon className={cn(`size-5 text-${isFocusedColor}`)} />
        </div>
      )}

      <div className="flex flex-col px-2 w-full duration-200 py-[4.5px]">
        <label
          className={cn(
            "font-bold duration-200 text-primary text-base h-[21px]",
            !query ? "text-gray-400" : "text-gray-400 text-xs"
          )}
        >
          {label}
        </label>

        <input
          id={id}
          ref={inputRef}
          type="text"
          value={query}
          onChange={onChange}
          onFocus={handleFocus}
          className={cn(
            "text-primary duration-200 w-full outline-none font-bold text-base",
            query || isFocused ? "h-[21px] opacity-100" : "h-[0px] opacity-0"
          )}
        />
      </div>

      <div className="h-full pl-2 flex justify-center items-center border-l border-slate-300">
        {!isFocused ? (
          <ChevronDownIcon className="size-6 text-slate-300" />
        ) : (
          <ChevronUpIcon className="size-6 text-slate-300" />
        )}
      </div>
    </div>
  )
}

interface QuoteDropdownProps {
  onClose: () => void;
  items: any[];
  footer?: ReactNode;
}

export function QuoteDropdown(props: QuoteDropdownProps) {
  const { onClose, items, footer } = props;
  const { isFocused, icon, setSelectedData, selectedData } =
    useQuoteAutocomplete();

  if (!isFocused) return null;

  return (
    <div className="w-full flex flex-col">
      <Dropdown
        icon={icon}
        loading={false}
        onClose={onClose}
        selectedData={selectedData}
        setSelectedData={(val) => setSelectedData(val)}
        data={items}
        height={footer ? 150 : undefined}
      />
      {footer && <div className="w-full px-6 pb-2">{footer}</div>}
    </div>
  );
}
